import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { CasinoGame } from 'types/CasinoGame';
import { casinoProviderLabel } from 'helpers/casinoProvider';
import styles from './ModernCasinoBrowser.module.css';

interface ProviderGames {
  provider: string;
  games: CasinoGame[];
}

interface Props {
  providers: ProviderGames[];
  /** Renders one playable tile (the list owns launching a game). */
  tile: (game: CasinoGame) => ReactNode;
  /** A search is on: show only the grid of matches. */
  searching: boolean;
  /** The player's starred games, newest first: a rail above the others. */
  favorites?: CasinoGame[];
}

const PAGE = 40;
const RAIL = 12;

/** Modern-layout casino lobby: top and new games rails, type and provider
 *  chips, and a two-column grid that grows on demand. */
export function ModernCasinoBrowser({
  providers,
  tile,
  searching,
  favorites = [],
}: Props) {
  const { t } = useTranslation();
  const [type, setType] = useState('');
  const [provider, setProvider] = useState('');
  const [shown, setShown] = useState(PAGE);

  const all = useMemo(() => providers.flatMap((p) => p.games), [providers]);
  const types = useMemo(() => {
    const counts = new Map<string, number>();
    all.forEach(
      (g) => g.type && counts.set(g.type, (counts.get(g.type) ?? 0) + 1)
    );
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([k]) => k);
  }, [all]);
  const providerNames = useMemo(
    () => providers.filter((p) => p.games.length > 0).map((p) => p.provider),
    [providers]
  );
  const newest = useMemo(
    () =>
      [...all]
        .sort((a, b) => (b.updated_at ?? 0) - (a.updated_at ?? 0))
        .slice(0, RAIL),
    [all]
  );
  const filtered = useMemo(
    () =>
      all.filter(
        (g) =>
          (!type || g.type === type) && (!provider || g.provider === provider)
      ),
    [all, type, provider]
  );

  const chip = (active: boolean, label: string, onClick: () => void) => (
    <button
      type="button"
      key={label}
      className={classNames(styles.chip, active && styles.chipActive)}
      onClick={() => {
        onClick();
        setShown(PAGE);
      }}
    >
      {label}
    </button>
  );

  return (
    <div className={styles.lobby}>
      {!searching && favorites.length > 0 && (
        <Rail icon="⭐" title={t('casino_favorites')}>
          {favorites.map(tile)}
        </Rail>
      )}
      {!searching && all.length > 0 && (
        <>
          <Rail icon="🔥" title={t('casino_top_games')}>
            {all.slice(0, RAIL).map(tile)}
          </Rail>
          <Rail icon="🆕" title={t('casino_new_games')}>
            {newest.map(tile)}
          </Rail>
        </>
      )}

      {!searching && types.length > 1 && (
        <div className={styles.chips}>
          {chip(!type, t('casino_all'), () => setType(''))}
          {types.map((x) =>
            chip(
              type === x,
              t(`casino_type_${x.replace(/\s+/g, '_')}`, { defaultValue: x }),
              () => setType(x)
            )
          )}
        </div>
      )}
      {!searching && providerNames.length > 1 && (
        <div className={styles.chips}>
          {chip(!provider, t('casino_all_providers'), () => setProvider(''))}
          {providerNames.map((x) =>
            chip(provider === x, casinoProviderLabel(x), () => setProvider(x))
          )}
        </div>
      )}

      <div className={styles.grid}>{filtered.slice(0, shown).map(tile)}</div>
      {filtered.length > shown && (
        <button
          type="button"
          className={styles.more}
          onClick={() => setShown((n) => n + PAGE)}
        >
          {t('casino_show_more', { count: filtered.length - shown })}
        </button>
      )}
      {filtered.length === 0 && (
        <p className={styles.empty}>{t('casino_no_games')}</p>
      )}
    </div>
  );
}

/** A titled sideways row of tiles. Arrows beside the title page through it:
 *  a desktop mouse has no sideways swipe. */
function Rail({
  icon,
  title,
  children,
}: {
  icon: string;
  title: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [edge, setEdge] = useState({ start: true, end: true });
  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setEdge({
      start: el.scrollLeft <= 2,
      end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 2,
    });
  }, []);
  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure, children]);
  const page = (dir: 1 | -1) =>
    ref.current?.scrollBy({
      left: dir * ref.current.clientWidth * 0.85,
      behavior: 'smooth',
    });

  return (
    <>
      <div className={styles.railHead}>
        <h3 className={styles.railTitle}>
          <span aria-hidden>{icon}</span> {title}
        </h3>
        {!(edge.start && edge.end) && (
          <div className={styles.railArrows}>
            <button
              type="button"
              disabled={edge.start}
              onClick={() => page(-1)}
              aria-label={t('previous_label', { defaultValue: 'Previous' })}
            >
              ‹
            </button>
            <button
              type="button"
              disabled={edge.end}
              onClick={() => page(1)}
              aria-label={t('next_label', { defaultValue: 'Next' })}
            >
              ›
            </button>
          </div>
        )}
      </div>
      <div ref={ref} className={styles.rail} onScroll={measure}>
        {children}
      </div>
    </>
  );
}
